import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Clock, Loader2, Calendar } from "lucide-react";
import { useAuth } from "@/hooks/useAuth";
import { useQuery } from "@tanstack/react-query";
import { attendanceService } from "@/services/attendanceService";
import { AttendanceRecord } from "@/types/attendance";
import dayjs from "dayjs";

const AttendanceList = () => {
  const { company } = useAuth();
  const slug = company?.slug || "";

  const { data: attendanceResponse, isLoading } = useQuery({
    queryKey: ["attendance", slug],
    queryFn: () => attendanceService.getAttendance(slug),
    enabled: !!slug,
  });

  const records: AttendanceRecord[] = attendanceResponse?.data?.data || [];

  const formatTime = (dateTimeStr: string) => {
    if (!dateTimeStr) return "---";
    return dayjs(dateTimeStr).format("h:mm A");
  };

  const formatDate = (dateStr: string) => {
    return dayjs(dateStr).format("DD/MM/YYYY");
  };

  const formatOvertime = (minutes: number) => {
    if (!minutes || minutes <= 0) return "---";
    const h = Math.floor(minutes / 60);
    const m = minutes % 60;
    if (h === 0) return `+${m}m`;
    if (m === 0) return `+${h}h`;
    return `+${h}h ${m}m`;
  };

  if (isLoading) {
    return (
      <Card className="border-slate-200/60 shadow-sm bg-white">
        <CardContent className="flex justify-center py-16">
          <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="border-slate-200/60 shadow-sm bg-white overflow-hidden">
      <CardHeader className="pb-2 border-b border-slate-50">
        <CardTitle className="text-lg flex items-center gap-2 text-slate-900">
          <Clock className="h-5 w-5 text-blue-600" />
          Registros de Asistencia
        </CardTitle>
      </CardHeader>
      <CardContent className="p-0">
        <Table>
          <TableHeader>
            <TableRow className="bg-slate-50/50 hover:bg-slate-50/50 border-slate-100">
              <TableHead className="text-slate-600 font-semibold px-6 h-12">Empleado</TableHead>
              <TableHead className="text-slate-600 font-semibold h-12">Fecha</TableHead>
              <TableHead className="text-slate-600 font-semibold h-12">Horario Prog.</TableHead>
              <TableHead className="text-slate-600 font-semibold h-12">Entrada</TableHead>
              <TableHead className="text-slate-600 font-semibold h-12">Salida</TableHead>
              <TableHead className="text-slate-600 font-semibold h-12">Horas Extra</TableHead>
              <TableHead className="text-slate-600 font-semibold px-6 text-right h-12">Estado</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {records.map((record) => (
              <TableRow key={record.id} className="border-slate-100 hover:bg-slate-50/30 transition-colors">
                <TableCell className="font-semibold text-slate-900 px-6 py-4">
                  {record.employee?.name || "Desconocido"}
                </TableCell>
                <TableCell className="text-slate-600">
                  <div className="flex items-center gap-2">
                    <Calendar className="h-3.5 w-3.5 text-slate-400" />
                    {formatDate(record.date)}
                  </div>
                </TableCell>
                <TableCell className="text-slate-500 text-xs">
                  {record.scheduled_start_time ? (
                    <div className="flex flex-col">
                      <span>{record.scheduled_start_time.substring(0, 5)} - {record.scheduled_end_time?.substring(0, 5)}</span>
                    </div>
                  ) : (
                    <span className="italic">N/A</span>
                  )}
                </TableCell>
                <TableCell className="text-slate-700 font-bold">
                  {formatTime(record.check_in)}
                </TableCell>
                <TableCell className="text-slate-700 font-bold">
                  {formatTime(record.check_out)}
                </TableCell>
                <TableCell>
                  {record.overtime_minutes > 0 ? (
                    <Badge variant="secondary" className="bg-amber-50 text-amber-700 border-amber-100 px-2 font-bold whitespace-nowrap">
                      {formatOvertime(record.overtime_minutes)}
                    </Badge>
                  ) : (
                    <span className="text-slate-400 text-xs">---</span>
                  )}
                </TableCell>
                <TableCell className="px-6 text-right">
                  <Badge className={`
                    ${record.status === 'present' ? 'bg-emerald-50 text-emerald-700 border-emerald-100' : 'bg-rose-50 text-rose-700 border-rose-100'} 
                    font-bold px-3 py-1 rounded-full uppercase text-[10px] tracking-wider
                  `}>
                    {record.status === 'present' ? 'Presente' : record.status}
                  </Badge>
                </TableCell>
              </TableRow>
            ))}
            {records.length === 0 && (
              <TableRow>
                <TableCell colSpan={6} className="text-center py-20 text-slate-400 italic font-light">
                  No hay registros de asistencia para mostrar.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
};

export default AttendanceList;
